import {
  DOCUMENT_TYPES,
  type DocumentStatus,
  type DocumentType,
  isSealed,
} from './constants.js';

/**
 * Máquinas de estado por tipo de documento.
 *
 * Ningún documento salta de estado por accidente: toda transición se declara
 * aquí, se valida en la API y deja rastro en auditoría.
 */

type Transitions = Partial<Record<DocumentStatus, DocumentStatus[]>>;

const COMMERCIAL_FLOW: Transitions = {
  draft: ['pending', 'issued', 'void'],
  pending: ['approved', 'rejected', 'draft'],
  approved: ['issued', 'draft'],
  issued: ['accepted', 'rejected', 'expired', 'corrected', 'void'],
  accepted: ['closed', 'corrected'],
  rejected: ['draft', 'closed'],
  expired: ['draft', 'closed'],
  corrected: [],
  void: [],
  closed: [],
};

const CONTRACT_FLOW: Transitions = {
  draft: ['pending', 'issued', 'void'],
  pending: ['approved', 'rejected', 'draft'],
  approved: ['issued', 'draft'],
  issued: ['signed', 'rejected', 'expired', 'corrected', 'void'],
  signed: ['closed', 'corrected', 'expired'],
  rejected: ['draft', 'closed'],
  expired: ['closed'],
  corrected: [],
  void: [],
  closed: [],
};

const INVOICE_FLOW: Transitions = {
  draft: ['pending', 'issued', 'void'],
  pending: ['approved', 'rejected', 'draft'],
  approved: ['issued', 'draft'],
  issued: ['partially_paid', 'paid', 'overdue', 'corrected', 'void'],
  partially_paid: ['paid', 'overdue', 'corrected', 'void'],
  overdue: ['partially_paid', 'paid', 'corrected', 'void'],
  paid: ['corrected', 'closed'],
  corrected: [],
  void: [],
  closed: [],
};

const SIMPLE_FLOW: Transitions = {
  draft: ['issued', 'void'],
  issued: ['closed', 'corrected', 'void'],
  corrected: [],
  void: [],
  closed: [],
};

export const STATE_MACHINES: Record<DocumentType, Transitions> = Object.fromEntries(
  DOCUMENT_TYPES.map((t) => {
    if (t === 'contract' || t === 'ncnda' || t === 'imfpa') return [t, CONTRACT_FLOW];
    if (t === 'invoice' || t === 'vendor_invoice') return [t, INVOICE_FLOW];
    if (['quote', 'loi', 'icpo', 'sco', 'fco', 'rfq', 'purchase_order'].includes(t))
      return [t, COMMERCIAL_FLOW];
    return [t, SIMPLE_FLOW];
  }),
) as Record<DocumentType, Transitions>;

export function canTransition(
  type: DocumentType,
  from: DocumentStatus,
  to: DocumentStatus,
): boolean {
  return (STATE_MACHINES[type]?.[from] ?? []).includes(to);
}

export function nextStatuses(type: DocumentType, from: DocumentStatus): DocumentStatus[] {
  return STATE_MACHINES[type]?.[from] ?? [];
}

/**
 * ¿Este documento se edita o se corrige?
 * Ésta es la diferencia entre un gestor de archivos y un registro auditable.
 */
export function editMode(status: DocumentStatus): 'edit' | 'correct' | 'locked' {
  if (status === 'draft' || status === 'pending') return 'edit';
  if (status === 'void' || status === 'corrected') return 'locked';
  return isSealed(status) ? 'correct' : 'edit';
}

/* ───────────────── Conversiones entre documentos (spec §22) ───────────── */

export interface ConversionRule {
  from: DocumentType;
  to: DocumentType;
  label: string;
  /** Estados de origen que habilitan la conversión. */
  requiresStatus: DocumentStatus[];
  /** Objetivo/capacidad que debe estar activa para permitirla. */
  requiresObjective?: string;
  /** Qué se hereda del documento origen. */
  inherits: string[];
}

export const CONVERSIONS: ConversionRule[] = [
  {
    from: 'quote', to: 'contract', label: 'Convertir en contrato',
    requiresStatus: ['accepted', 'issued'],
    requiresObjective: 'contract.issue',
    inherits: ['counterparty', 'lines', 'currency', 'scope', 'milestones', 'terms'],
  },
  {
    from: 'quote', to: 'invoice', label: 'Facturar cotización',
    requiresStatus: ['accepted'],
    inherits: ['counterparty', 'lines', 'currency', 'terms'],
  },
  {
    from: 'contract', to: 'invoice', label: 'Emitir factura del contrato',
    requiresStatus: ['signed'],
    inherits: ['counterparty', 'lines', 'currency', 'bank', 'terms', 'project'],
  },
  {
    from: 'contract', to: 'invoice', label: 'Facturar hito',
    requiresStatus: ['signed'],
    requiresObjective: 'project.milestones',
    inherits: ['counterparty', 'milestone', 'currency', 'bank'],
  },
  {
    from: 'invoice', to: 'payment', label: 'Registrar cobro',
    requiresStatus: ['issued', 'partially_paid', 'overdue'],
    inherits: ['counterparty', 'currency', 'balance'],
  },
  {
    from: 'payment', to: 'receipt', label: 'Generar recibo',
    requiresStatus: ['issued'],
    inherits: ['counterparty', 'currency', 'amount', 'allocations'],
  },
  {
    from: 'invoice', to: 'credit_note', label: 'Emitir nota de crédito',
    requiresStatus: ['issued', 'paid', 'partially_paid', 'overdue'],
    inherits: ['counterparty', 'lines', 'currency'],
  },
  {
    from: 'loi', to: 'fco', label: 'Responder con Full Corporate Offer',
    requiresStatus: ['issued', 'accepted'],
    requiresObjective: 'loi.issue',
    inherits: ['counterparty', 'product', 'quantity', 'incoterm', 'ports'],
  },
  {
    from: 'fco', to: 'contract', label: 'Convertir oferta en contrato',
    requiresStatus: ['accepted'],
    requiresObjective: 'loi.issue',
    inherits: ['counterparty', 'product', 'quantity', 'price', 'incoterm', 'terms'],
  },
  {
    from: 'purchase_request', to: 'purchase_order', label: 'Emitir orden de compra',
    requiresStatus: ['approved'],
    requiresObjective: 'purchase.flow',
    inherits: ['supplier', 'lines', 'currency'],
  },
  {
    from: 'expense', to: 'payment', label: 'Registrar pago del gasto',
    requiresStatus: ['issued'],
    inherits: ['counterparty', 'currency', 'amount'],
  },
];

export function conversionsFor(
  type: DocumentType,
  status: DocumentStatus,
): ConversionRule[] {
  return CONVERSIONS.filter((c) => c.from === type && c.requiresStatus.includes(status));
}

/* ────────────── Cadena de trazabilidad (spec §17) ───────────────────── */

/** Orden canónico del expediente. Alimenta la "cinta" de trazabilidad. */
export const TRACE_CHAIN: DocumentType[] = [
  'loi', 'quote', 'contract', 'invoice', 'payment', 'receipt',
];

export interface TraceLink {
  type: DocumentType;
  id?: string;
  number?: string;
  status?: DocumentStatus;
  /** Fecha de emisión (epoch ms) e importe, para el listado completo. */
  issueDate?: number | null;
  totalMinor?: number;
  present: boolean;
}

/**
 * Construye la cinta del expediente: qué eslabones existen y cuáles faltan.
 * Los eslabones ausentes se muestran como huecos, no se ocultan.
 */
export function buildTraceChain(
  docs: Array<{ id: string; type: DocumentType; number: string; status: DocumentStatus; issueDate?: number | null; totalMinor?: number }>,
  opts: { includeLoi?: boolean } = {},
): TraceLink[] {
  const chain = opts.includeLoi ? TRACE_CHAIN : TRACE_CHAIN.filter((t) => t !== 'loi');
  return chain.map((type) => {
    const doc = docs.find((d) => d.type === type);
    return doc
      ? { type, id: doc.id, number: doc.number, status: doc.status, issueDate: doc.issueDate ?? null, totalMinor: doc.totalMinor, present: true }
      : { type, present: false };
  });
}
